import { forwardRef } from 'react'
import { StagedPhotoGrid, StagedPhotoGridHandle } from './StagedPhotoGrid'
import { generateAircraftFacilityPhotoS3Key } from '@/lib/hooks/s3'
import {
  useAircraftAddFacilityPhoto,
  useAircraftRemoveFacilityPhoto,
} from '@/lib/hooks/aircraft'

interface FacilityPhotosProps {
  // Facility key the photos belong to (e.g. cateringGalley). Sent to the backend
  // and folded into the S3 key namespace.
  facility: string
  // S3 keys already persisted for this facility.
  s3Keys: string[]
  // Used for invalidation; absent in create mode until the aircraft is saved.
  aircraftId?: string
  readOnly?: boolean
  onPendingChange?: (count: number) => void
  trigger?: 'tile' | 'icon'
}

export type FacilityPhotosHandle = StagedPhotoGridHandle

// Thin binding of StagedPhotoGrid to the aircraft facility-photo endpoints.
export const FacilityPhotos = forwardRef<FacilityPhotosHandle, FacilityPhotosProps>(
  function FacilityPhotos(
    { facility, s3Keys, aircraftId, readOnly = false, onPendingChange, trigger },
    ref
  ) {
    const addPhoto = useAircraftAddFacilityPhoto(aircraftId ?? '')
    const removePhoto = useAircraftRemoveFacilityPhoto(aircraftId ?? '')

    return (
      <StagedPhotoGrid
        ref={ref}
        s3Keys={s3Keys}
        readOnly={readOnly}
        onPendingChange={onPendingChange}
        trigger={trigger}
        generateS3Key={(entityId, fileName) =>
          generateAircraftFacilityPhotoS3Key(entityId, facility, fileName)
        }
        addPhoto={(entityId, s3Key) =>
          addPhoto.mutateAsync({ id: entityId, facility, s3Key } as never)
        }
        removePhoto={(entityId, s3Key) =>
          removePhoto.mutateAsync({ id: entityId, facility, s3Key } as never)
        }
      />
    )
  }
)
